import { Typography, Table, Input, Button, message, Space, Tooltip } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface ConfigSetting { id: string; key: string; value: string; description: string; updatedAt: string }

export default function ConfigPage() {
  const qc = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: () => api.get('/admin/config'),
  });

  const updateConfig = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => api.patch(`/admin/config/${key}`, { value }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['config'] }); setEditingKey(null); message.success('Config updated'); },
    onError: () => message.error('Failed to update config'),
  });

  const settings: ConfigSetting[] = (data as { data: ConfigSetting[] })?.data ?? [];

  const columns = [
    {
      title: 'Key', dataIndex: 'key', key: 'key',
      render: (v: string) => <code style={{ fontSize: 12, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#0f172a' }}>{v}</code>,
    },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (v: string) => <span style={{ color: '#64748b' }}>{v}</span> },
    {
      title: 'Value', key: 'value', width: 280,
      render: (_: unknown, record: ConfigSetting) =>
        editingKey === record.key ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="cw-config-edit-input"
              style={{ width: 110, height: 30 }}
              autoFocus
              onPressEnter={() => updateConfig.mutate({ key: record.key, value: editValue })}
            />
            <Button
              type="primary"
              icon={<CheckOutlined />}
              className="cw-config-btn"
              loading={updateConfig.isPending}
              onClick={() => updateConfig.mutate({ key: record.key, value: editValue })}
            />
            <Button
              icon={<CloseOutlined />}
              className="cw-config-btn"
              onClick={() => setEditingKey(null)}
            />
          </div>
        ) : (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <Tooltip title={record.value.length > 30 ? record.value : undefined} placement="topLeft">
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', cursor: record.value.length > 30 ? 'help' : 'default', whiteSpace: 'nowrap' }}>
                {record.value.length > 30 ? `${record.value.slice(0, 30)}…` : record.value}
              </span>
            </Tooltip>
            <Button
              icon={<EditOutlined />}
              className="cw-config-btn cw-config-btn--edit"
              style={{ flexShrink: 0 }}
              onClick={() => { setEditingKey(record.key); setEditValue(record.value); }}
            >
              Edit
            </Button>
          </div>
        ),
    },
    {
      title: 'Updated', dataIndex: 'updatedAt', key: 'updatedAt',
      render: (d: string) => <span style={{ color: '#94a3b8', fontSize: 12 }}>{new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Config Settings</Typography.Title>
        <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Configurable platform limits and settings</Typography.Text>
      </div>

      {isLoading ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
      ) : isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {settings.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No settings found</div>
          ) : settings.map((record) => (
            <div key={record.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
              {/* Key */}
              <div style={{ marginBottom: 4 }}>
                <code style={{ fontSize: 12, background: '#f1f5f9', padding: '3px 8px', borderRadius: 6, color: '#0f172a', fontWeight: 600, wordBreak: 'break-all' }}>
                  {record.key}
                </code>
              </div>

              {/* Description */}
              {record.description && (
                <div style={{ color: '#64748b', fontSize: 13, marginBottom: 10 }}>
                  {record.description}
                </div>
              )}

              {/* Value row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                {editingKey === record.key ? (
                  <Space wrap>
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      style={{ width: 130 }}
                      autoFocus
                    />
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={() => updateConfig.mutate({ key: record.key, value: editValue })}
                      loading={updateConfig.isPending}
                    >
                      Save
                    </Button>
                    <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingKey(null)}>
                      Cancel
                    </Button>
                  </Space>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <Tooltip title={record.value.length > 30 ? record.value : undefined} placement="topLeft">
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', cursor: record.value.length > 30 ? 'help' : 'default' }}>
                        {record.value.length > 30 ? `${record.value.slice(0, 30)}…` : record.value}
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      style={{ flexShrink: 0 }}
                      onClick={() => { setEditingKey(record.key); setEditValue(record.value); }}
                    >
                      Edit
                    </Button>
                  </div>
                )}
                <span style={{ color: '#94a3b8', fontSize: 12 }}>
                  {new Date(record.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={settings}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
        />
      )}
    </div>
  );
}
